import { TranslationsWithId } from 'src/types';

enum OWNER_TYPE {
    PERSON = 'person',
    ORGANISATION = 'organisation',
}

export type Owner = {
    id: string;
    created_at: string;
    modified_at: string;
    type: OWNER_TYPE;
    name: string;
    identifier?: string;
};

export enum VISITOR_TYPE {
    PERSON = 'person',
    CAR = 'car',
    TRUCK = 'truck',
    BUS = 'bus',
    SPECIALISED = 'specialised',
}

export type Visitor = {
    id: string;
    owner: Owner;
    created_at: string;
    modified_at: string;
    type: VISITOR_TYPE;
    attributes: {
        [key: string]: string;
    };
    main_identifier: { value: string } | null;
    in_parking: boolean;
    zones: TranslationsWithId[];
    last_visit_date: string | null;
    total_visits_count: number;
};

export enum WebSocketEventMessages {
    NEW_LPR_DETECTION = 'new_lpr_detection',
    NEW_ACTIVE_STAY = 'new_active_stay',
    NEW_ACTIVE_STAY_LOG_EVENT = 'new_active_stay_log_event',
    NEW_ACTIVE_STAY_PERIOD = 'new_active_stay_period',
    NEW_ACTIVE_STAY_PERIOD_LOG_EVENT = 'new_active_stay_period_log_event',
    COMPLETED_ACTIVE_STAY = 'completed_active_stay',
    COMPLETED_ACTIVE_STAY_LOG_EVENT = 'completed_active_stay_log_event',
    COMPLETED_ACTIVE_STAY_PERIOD = 'completed_active_stay_period',
    COMPLETED_ACTIVE_STAY_PERIOD_LOG_EVENT = 'completed_active_stay_period_log_event',
    NEW_ENTRY_PASS = 'new_entry_pass',
    NEW_EXIT_PASS = 'new_exit_pass',
    GATEWAY_STATE_CHANGED = 'gateway_state_changed',
    GATEWAY_SENSOR_STATE_CHANGED = 'gateway_sensor_state_changed',

    NEW_PAY_STATION_EVENT = 'new_pay_station_event',
    NEW_PAY_STATION_TRANSACTION = 'new_pay_station_transaction',
    PAY_STATION_OPERATION_CHANGE = 'pay_station_operation_change',
    PAY_STATION_SCREEN_CHANGE = 'pay_station_screen_change',
    PAY_STATION_CASH_LEVEL_CHANGE = 'pay_station_cash_level_change',

    HEALTH_STATUS_UPDATED = 'health_status_updated',
}

export enum HealthSubjectKind {
    COMPONENT = 'component',
    PAY_STATION = 'pay_station',
}

export enum HealthStatus {
    OPERATIONAL = 'operational',
    WARNING = 'warning',
    ERROR = 'error',
    NO_CONNECTION = 'no_connection',
}

export type NewTypeDetection = {
    message: WebSocketEventMessages.NEW_LPR_DETECTION;
    data: {
        gateway_id: string;
        license_plate: string;
    };
};

export type NewPass = {
    message: WebSocketEventMessages.NEW_ENTRY_PASS | WebSocketEventMessages.NEW_EXIT_PASS;
    data: {
        gateway_pass_id: string;
        gateway_id: string;
        visitor_id: string;
        identifiers: string;
    };
};

export enum GatewayAction {
    OPEN = 'open',
    LOCK = 'lock',
    EMERGENCY = 'emergency',
}

export type GatewayActionState = {
    gateway: {
        id: string;
        name: string;
    };
    action: GatewayAction;
} | null;

export type PayStationChange = {
    message:
        | WebSocketEventMessages.PAY_STATION_SCREEN_CHANGE
        | WebSocketEventMessages.PAY_STATION_CASH_LEVEL_CHANGE
        | WebSocketEventMessages.PAY_STATION_OPERATION_CHANGE
        | WebSocketEventMessages.NEW_PAY_STATION_EVENT
        | WebSocketEventMessages.NEW_PAY_STATION_TRANSACTION;
    data: {
        pay_station_id: string;
    };
};

export type HealthStatusUpdated = {
    message: WebSocketEventMessages.HEALTH_STATUS_UPDATED;
    data: {
        subject: {
            kind: HealthSubjectKind;
            id: string;
        };
        status: HealthStatus;
    };
};

export type NewActiveStay = {
    message:
        | WebSocketEventMessages.NEW_ACTIVE_STAY
        | WebSocketEventMessages.NEW_ACTIVE_STAY_LOG_EVENT
        | WebSocketEventMessages.NEW_ACTIVE_STAY_PERIOD
        | WebSocketEventMessages.NEW_ACTIVE_STAY_PERIOD_LOG_EVENT
        | WebSocketEventMessages.COMPLETED_ACTIVE_STAY
        | WebSocketEventMessages.COMPLETED_ACTIVE_STAY_LOG_EVENT
        | WebSocketEventMessages.COMPLETED_ACTIVE_STAY_PERIOD
        | WebSocketEventMessages.COMPLETED_ACTIVE_STAY_PERIOD_LOG_EVENT;
    data: {
        stay_id: string;
    };
};

export type GatewayStateChanged = {
    message: WebSocketEventMessages.GATEWAY_STATE_CHANGED;
    data: {
        gateway_id: string;
        state: 'opened' | 'closed';
        state_changed_at: string;
    };
};

export type GatewaySensorStateChanged = {
    message: WebSocketEventMessages.GATEWAY_SENSOR_STATE_CHANGED;
    data: {
        gateway_id: string;
        /** Gateway-level aggregate (any sensor activated → busy), when the backend computes it.
         *  Optional: older backends omit it, so the FE aggregates `sensor.activated` itself. */
        is_busy?: boolean;
        /** The individual sensor edge that triggered this event. */
        sensor?: {
            id: string;
            position: string;
            activated: boolean;
            changed_at: string;
        };
    };
};

export type WebSocketEvent =
    | NewTypeDetection
    | NewPass
    | PayStationChange
    | NewActiveStay
    | HealthStatusUpdated
    | GatewayStateChanged
    | GatewaySensorStateChanged;
